/**
 * @file The feature selection component. It returns a view that give user the opportunity to select the features they
 * want to install.
 * @author Haonan Yu(haoyu@microStrategy.com)
 */

import { Form, Divider, Row, Col, Tree, Input } from 'antd';
import React from 'react';
//data & funcs
import {
    featureDependencies,
    featureDirectory2StateMap,
    featureKey2StateMap,
} from '../../../../Utils/LinuxDataLoader';
import {
    createTree,
    initialMaps,
    checkNode,
    uncheckNode,
    getInitialCheckedKeys,
    selectAll,
    unselectAll,
} from '../../../../Utils/CreateTree';
import {
    M11_4_12,
    M11_5_03,
    M11_5_06,
    M11_5_09,
    M11_5_12,
    M11_6_03,
    M11_6_06,
    M11_6_09,
    LIN_FEATURESELECTION,
} from '../../../../Utils/Constants';

//component
import ButtonBar from '../../../ButtonBar/MainButtonBar/MainButtonBar';
import SelectAllButtonBar from '../../../ButtonBar/SelectAllButtonBar/SelectAllButtonBar';
import Navigation from '../../../Navigation/Navigation';
import ReferencePicture from '../../../ReferencePicture/ReferencePicture';
//css
import 'antd/dist/antd.css';
import '../../../../Utils/CommonCss.css';

const PAGE_NAME = LIN_FEATURESELECTION;

class FeatureSelection extends React.Component {
    /**
     * Contains 5 maps to control the checkbox tree
     * @type {Object}
     */
    maps = {
        /**
         * A map to mapping nodes and their children
         * key: nodeKey, value: [nodeKey, nodeKey, ...]
         * @type {Map}
         */
        childrenMap: new Map(),
        /**
         * A map to mapping a node to its parent
         * key: nodeKey, value: nodeKey
         * @type {Map}
         */
        parentMap: new Map(),

        /**
         * A map to mapping nodes and their dependencies
         * key: nodeKey, value: [nodeKey, nodeKey, ...]
         * @type {Map}
         */
        dependencyMap: new Map(),

        /**
         * A map to store the disable status of each node
         * key: nodeKey, value: boolean
         * true, the node will be set as disSelectable,
         * false, otherwise
         * @type {Map}
         */
        disSelectableMap: new Map(),

        /**
         *  Mapping the key of TreeNode to states in PageObjects
         * the key is the value of 'key' for each TreeNode in FeatureSelection Page
         * the value is the corresponding state of that TreeNode
         * @type {Map}
         */
        feature2StateMap: featureKey2StateMap,
    };

    nextStep = () => {
        if (this.checkedKeys.checked.length > 0) {
            this.props.nextStep();
        } else {
            this.showErrorWarning();
            this.forceUpdate();
        }
    };

    backStep = () => {
        this.props.backStep();
    };

    /**
     * Show the Error Warning
     */
    showErrorWarning = () => {
        this.featureContainerStyle = [
            'featureScrollableContainer',
            'featureScrollableContainerError',
        ];
        this.btnBarStyle = 'selectBtnBarError';
        this.showErrorMsg = true;
    };

    /**
     * Remove the Error Warning
     */
    removeErrorWarning = () => {
        this.featureContainerStyle = 'featureScrollableContainer';
        this.btnBarStyle = 'selectBtnBar';
        this.showErrorMsg = false;
    };

    /**
     * To handle the special cases for the installation on Linux Platform when do the checking action
     * @param {String} nodeKey the unique id of the TreeNode
     * @param {Object} checkedKeys the object that 'antd' treeSelect use to control the UI
     */
    m2021SpecialCheckingCaseHandler = (nodeKey, checkedKeys) => {
        // any 1.x is checked, 1.5 and 1.6 are required and should be set disSelectable
        if (nodeKey.match(/^1$|^1\..$/)) {
            if (!checkedKeys.checked.includes('1.5')) {
                // no corresponding state for this product
                checkedKeys.checked.push('1.5');
            }
            if (!checkedKeys.checked.includes('1.6')) {
                // no corresponding state for this product
                checkedKeys.checked.push('1.6');
            }
            this.maps.disSelectableMap.set('1.5', true);
            this.maps.disSelectableMap.set('1.6', true);
        } else if (nodeKey === '3') {
            //Special case for remote repo
            this.props.setOriginState('showRemoteRepoPage', false);
            this.props.setOriginState('remoteRepositoryDBHost', '');
            this.props.setOriginState('remoteRepositoryDBPort', '');
            this.props.setOriginState('remoteRepositoryDBUser', '');
            this.props.setOriginState('remoteRepositoryDBPassword', '');
            this.props.setOriginState('platformAnalyticsOverrideDb', '');
            this.props.setOriginState('collaborationOverrideDb', '');
            this.props.setOriginState('storageServiceOverrideDb', '');
        } else if (nodeKey === '4.2' || nodeKey === '4' || nodeKey === '13') {
            //Special case for remote repo
            if (!checkedKeys.checked.includes('3')) {
                this.props.setOriginState('showRemoteRepoPage', true);
            }
        } else {
            /* the basic checkNode will set Web Universal disSelectable due to its internal 
                dependencies. Should always try to set it false unless 16 is checked*/
            this.maps.disSelectableMap.set('2', false);
        }
    };

    /**
     * To handle the special cases for the installation on Linux Platform when do the unChecking action
     * @param {String} nodeKey the unique id of the TreeNode
     * @param {Object} checkedKeys the array that 'antd' treeSelect use to control the UI
     */
    m2021SpecialUnCheckingCaseHandler = (nodeKey, checkedKeys) => {
        if (nodeKey === '1') {
            this.maps.disSelectableMap.set('1.5', false);
            this.maps.disSelectableMap.set('1.6', false);
        } else if (nodeKey === '3') {
            //Special case for remote repo
            if (
                checkedKeys.checked.includes('4.2') ||
                checkedKeys.checked.includes('13')
            ) {
                this.props.setOriginState('showRemoteRepoPage', true);
            }
        } else if (nodeKey === '4.2' || nodeKey === '4' || nodeKey === '13') {
            //Special case for remote repo
            if (!checkedKeys.checked.includes('3')) {
                if (
                    !checkedKeys.checked.includes('4.2') &&
                    !checkedKeys.checked.includes('13')
                ) {
                    this.props.setOriginState('showRemoteRepoPage', false);
                    this.props.setOriginState('remoteRepositoryDBHost', '');
                    this.props.setOriginState('remoteRepositoryDBPort', '');
                    this.props.setOriginState('remoteRepositoryDBUser', '');
                    this.props.setOriginState('remoteRepositoryDBPassword', '');
                    this.props.setOriginState('platformAnalyticsOverrideDb', '');
                    this.props.setOriginState('collaborationOverrideDb', '');
                    this.props.setOriginState('storageServiceOverrideDb', '');
                }
            }
        }
    };

    /**
     * event triggered when checkbox is being checked or unchecked.
     * if the box is being checked, make all of its children and dependencies checked. The dependencies should be disSelectable unless the root node has been unchecked
     * if the box is being unchecked, uncheck all of its children. Set its dependencies selectable
     * @event
     * @param {Object} checkedKeys the Object that 'antd' treeSelect use to control the UI
     * @param {string} info  the information of the node that trigger this event
     */
    onCheck = (checkedKeys, info) => {
        let nodeKey = info.node.props.eventKey;
        if (info.checked === true) {
            checkNode(nodeKey, checkedKeys, this.maps, this.props);
            this.specialCheckingCaseHandler(nodeKey, checkedKeys);
        } else {
            uncheckNode(nodeKey, checkedKeys, this.maps, this.props);
            this.specialUnCheckingCaseHandler(nodeKey, checkedKeys);
        }

        /*If none products checked and the error message showed, the next
          onCheck call must be the user checked something, so remove the error*/
        if (this.showErrorMsg) {
            this.removeErrorWarning();
        }
    };

    /**
     * event triggered when TreeNode is being Selected.
     * If a specific feature is selected, a input box will pop-up for user to modify the install directory of that feature
     * This function controls the content of 'featureDirectInput' which will rendered when changes
     * @event
     * @param {Object} selectedKeys the array that 'antd' treeSelect use to control the UI
     * @param {string} info  the information of the node that trigger this event
     */
    onSelectNode = (selectedKeys, info) => {
        let nodeKey = info.node.props.eventKey;
        this.setState({
            dirState: featureDirectory2StateMap.get(nodeKey),
        });
    };

    constructor(props) {
        super(props);
        /* A state to control the visibility of the input box for product install directory.
           When the onSelect product have option to change its install directory,
           this state will store the name of that state.
        */
        this.state = {
            dirState: null,
        };
        switch (props.useVersion) {
            case M11_4_12:
            case M11_5_03:
            case M11_5_06:
            case M11_5_09:
            case M11_5_12:
            case M11_6_03:
            case M11_6_06:
            case M11_6_09:
                this.specialCheckingCaseHandler =
                    this.m2021SpecialCheckingCaseHandler;
                this.specialUnCheckingCaseHandler =
                    this.m2021SpecialUnCheckingCaseHandler;
                break;
            default:
        }
        // An object to control the status of checkboxes in the tree
        this.checkedKeys = {};
        this.checkedKeys.checked = [];
        // default root key ''
        initialMaps(featureDependencies, '', this.maps);
        // initiate the checkedKeys
        getInitialCheckedKeys(
            this.checkedKeys,
            this.maps,
            this.specialCheckingCaseHandler,
            this.props
        );

        // Control error message when non product selected and tried to go next
        this.featureContainerStyle = 'featureScrollableContainer';
        this.btnBarStyle = 'selectBtnBar';
        this.showErrorMsg = false;
    }

    render() {
        let featureDirectInput = null;

        if (this.state.dirState === undefined || this.state.dirState === null) {
            featureDirectInput = null;
        } else {
            featureDirectInput = (
                <div>
                    <p>Set the install directory of the selected feature:</p>
                    <Input
                        value={this.props.getOriginState(this.state.dirState)}
                        onChange={(e) => {
                            this.props.setOriginState(
                                this.state.dirState,
                                e.target.value
                            );
                        }}
                    />
                </div>
            );
        }
        return (
            <Form className="form">
                <Row type="flex" align="top">
                    <Col
                        span={4}
                        className={['colNav', 'linFeatureNavHeight'].join(' ')}
                    >
                        <p className="headlineTop">Steps</p>
                        <Navigation
                            setOriginState={this.props.setOriginState}
                            getOriginState={this.props.getOriginState}
                            steps={this.props.steps}
                            deepestStep={this.props.deepestStep}
                            useOs={this.props.useOs}
                        />
                    </Col>

                    <Col span={12} className="colContent">
                        <p className="headlineTop">Feature Selection</p>
                        <Tree
                            checkedKeys={this.checkedKeys}
                            className={this.featureContainerStyle}
                            checkable
                            checkStrictly
                            onCheck={(checkedKeys, e) =>
                                this.onCheck(this.checkedKeys, e)
                            }
                            onSelect={this.onSelectNode}
                        >
                            {createTree(
                                featureDependencies,
                                this.maps.disSelectableMap
                            )}
                        </Tree>
                        <SelectAllButtonBar
                            styleName={this.btnBarStyle}
                            showErrorMsg={this.showErrorMsg}
                            selectAll={() => {
                                selectAll(
                                    this.checkedKeys,
                                    this.maps,
                                    this.specialCheckingCaseHandler,
                                    this.props
                                );
                                // remove warning if exists
                                if (this.showErrorMsg) {
                                    this.removeErrorWarning();
                                }
                            }}
                            unselectAll={() => {
                                unselectAll(
                                    this.checkedKeys,
                                    this.maps,
                                    this.specialUnCheckingCaseHandler,
                                    this.props
                                );
                            }}
                        />
                        <br />
                        {featureDirectInput}
                    </Col>

                    <Divider
                        className="divider"
                        type="vertical"
                        orientation="right"
                    />
                    <Col span={7} className="colPics">
                        <p className="headlineTop">Reference Pictures</p>
                        <ReferencePicture
                            pageName={PAGE_NAME}
                            getOriginState={this.props.getOriginState}
                        />
                    </Col>
                </Row>
                <ButtonBar
                    useOs={this.props.useOs}
                    clickNext={this.nextStep}
                    clickBack={this.backStep}
                />
            </Form>
        );
    }
}

export default FeatureSelection;
